import type { AutomationEvent, AutomationEventType } from '../types'

type Listener = (event: AutomationEvent) => void

/**
 * Lightweight event bus for automation lifecycle (event-driven architecture).
 */
export class AutomationEventBus {
  private readonly listeners = new Map<AutomationEventType | '*', Set<Listener>>()
  private readonly history: AutomationEvent[] = []

  on(type: AutomationEventType | '*', listener: Listener): () => void {
    const set = this.listeners.get(type) ?? new Set()
    set.add(listener)
    this.listeners.set(type, set)
    return () => set.delete(listener)
  }

  emit(
    type: AutomationEventType,
    message: string,
    meta?: { supplierId?: string; jobId?: string; sessionId?: string },
  ): AutomationEvent {
    const event: AutomationEvent = {
      id: crypto.randomUUID(),
      type,
      message,
      supplierId: meta?.supplierId,
      jobId: meta?.jobId,
      sessionId: meta?.sessionId,
      createdAt: new Date().toISOString(),
    }
    this.history.unshift(event)
    if (this.history.length > 200) this.history.pop()

    for (const listener of this.listeners.get(type) ?? []) listener(event)
    for (const listener of this.listeners.get('*') ?? []) listener(event)
    return event
  }

  list(limit = 50): AutomationEvent[] {
    return this.history.slice(0, limit)
  }

  clear(): void {
    this.history.length = 0
  }
}

export const automationEventBus = new AutomationEventBus()
